import { firebaseService } from '../config/firebase';
import { authService } from './authService';
import { Chat, Message } from '@/types/chat';
import { apiService } from './apiService';
import { Vehicle } from '@/types/vehicle';
import { feedbackService } from './feedbackService';
import { vehicleService } from './vehicleService';

class ChatService {
  private readonly COLLECTION_NAME = 'chats';

  // Ensure backward compatibility for chats missing the `public` flag
  private ensurePublicFlag<T extends { public?: boolean }>(chat: T): T & { public: boolean } {
    return { ...chat, public: chat.public ?? false };
  }

  async createNewChat(vehicle: Vehicle, token: string|null = ""): Promise<Chat> {
    const user = authService.getCurrentUser();
    if (!user) {
      throw new Error('User must be authenticated to create a chat');
    }

    try {
      const chatId = await apiService.startSession(vehicle, token);
      const chat: Chat = {
        id: chatId,
        title: 'New Chat',
        messages: [],
        userId: user.uid,
        vehicleId: vehicle.id,
        public: false, // default to not publicly shared
        createdAt: Date.now(),
        updatedAt: Date.now()
      };

      await firebaseService.saveDocument(this.COLLECTION_NAME, chat.id, chat);
      return chat;
    } catch (error) {
      apiService.handleError(error as Error | Response);
      throw error;
    }
  }

  async getAllChats(): Promise<Chat[]> {
    const user = authService.getCurrentUser();
    if (!user) {
      throw new Error('User must be authenticated to get chats');
    }

    try {
      const conditions = [['userId', '==', user.uid]];
      const orderByFields = [['updatedAt', 'desc']];
      const results = await firebaseService.getDocuments(this.COLLECTION_NAME, conditions, orderByFields);
      // Normalize to ensure `public` is present (defaults false for older records)
      return (results as any[]).map((c) => this.ensurePublicFlag(c)) as Chat[];
    } catch (error) {
      apiService.handleError(error as Error | Response);
      throw error;
    }
  }

  async getChat(chatId: string): Promise<Chat | null> {
    const user = authService.getCurrentUser();
    if (!user) {
      throw new Error('User must be authenticated to get a chat');
    }

    try {
      const raw = await firebaseService.getDocument(this.COLLECTION_NAME, chatId) as any | null;
      if (raw && raw.userId !== user.uid) {
        throw new Error('Unauthorized access to chat');
      }

      const chat = raw ? (this.ensurePublicFlag(raw) as Chat) : null;
      return chat;
    } catch (error) {
      apiService.handleError(error as Error | Response);
      throw error;
    }
  }

  /**
   * Fetch a chat for public viewing without requiring authentication.
   * Returns chat only if it exists and has public flag true, else null.
   */
  async getPublicChat(chatId: string): Promise<Chat | null> {
    try {
      const raw = await firebaseService.getDocument(this.COLLECTION_NAME, chatId) as any | null;
      if (!raw) return null;
      const chatWithId = { id: chatId, ...raw }; // ensure id is present for public fetches
      const chat = this.ensurePublicFlag(chatWithId) as Chat;
      if (!Array.isArray(chat.messages)) (chat as any).messages = [];
      if (!chat.public) return null;
      return chat;
    } catch (error) {
      console.error('Error fetching public chat:', error);
      return null; // For public fetch, swallow errors and return null to avoid leaking details
    }
  }

  async saveChat(chat: Chat): Promise<void> {
    const user = authService.getCurrentUser();
    if (!user) {
      throw new Error('User must be authenticated to save a chat');
    }

    if (chat.userId !== user.uid) {
      throw new Error('Unauthorized access to chat');
    }

    try {
      const toSave: Chat = this.ensurePublicFlag({ ...chat });
      toSave.updatedAt = Date.now();
      await firebaseService.saveDocument(this.COLLECTION_NAME, toSave.id, toSave);
    } catch (error) {
      apiService.handleError(error as Error | Response);
      throw error;
    }
  }

  /**
   * Rebuild session context from chat history
   * @param chat The chat to rebuild context for
   * @param token Authentication token
   * @returns Updated chat with new session ID
   */
  async rebuildChatSession(chat: Chat, token: string | null = ""): Promise<Chat> {
    const user = authService.getCurrentUser();
    if (!user) {
      throw new Error('User must be authenticated to rebuild chat session');
    }

    try {
      // Get vehicle information from the chat using the proper vehicle service
      const vehicle = await vehicleService.getVehicleById(chat.vehicleId, token || undefined);

      // Prepare message history for rebuilding
      const messageHistory = chat.messages.map(msg => ({
        role: msg.role,
        content: msg.content,
        timestamp: msg.timestamp
      }));

      // Rebuild the session
      const newSessionId = await apiService.rebuildSession(vehicle, messageHistory, token);
      
      // Update the chat with new session ID
  const updatedChat = this.ensurePublicFlag({ ...chat, id: newSessionId });
      
      // Save the updated chat to Firebase with new ID
  await firebaseService.saveDocument(this.COLLECTION_NAME, newSessionId, updatedChat);
      
      // Delete the old chat record
      await firebaseService.deleteDocument(this.COLLECTION_NAME, chat.id);
      
      console.log(`Session rebuilt: ${chat.id} -> ${newSessionId}`);
      return updatedChat;
    } catch (error) {
      console.error('Failed to rebuild chat session:', error);
      throw error;
    }
  }

  async addMessage(
    chatId: string,
    message: Message,
    onStream?: (chunk: string) => void,
    token: string | null = "",
    onTitleUpdate?: (title: string) => void
  ): Promise<Chat> {
    const chat = await this.getChat(chatId);
    if (!chat) {
      throw new Error('Chat not found');
    }

    // Get a title for the chat if this is the first message (non-blocking)
    if (chat.messages.length === 0) {
      // Start title fetching in background without awaiting
      apiService.getChatTitle(message.content, token)
        .then(title => {
          if (title && title !== chat.title) {
            chat.title = title;
            // Save the updated title asynchronously
            this.saveChat({ ...chat }).catch(error => {
              console.error('Failed to save updated chat title:', error);
            });
            // Notify the UI of the title update
            if (onTitleUpdate) {
              onTitleUpdate(title);
            }
          }
        })
        .catch(error => {
          console.warn('Failed to get chat title:', error);
          // Title will remain as 'New Chat'
        });
    }

    // Add user's message
    chat.messages.push(message);
    chat.updatedAt = Date.now();

    // Send user's message and handle streaming response
    let streamedResponse = "";
    let currentChatId = chatId;

    const streamHandler = (chunk: string) => {
      // Catch "heartbeats" sent to keep the connection alive
      if (chunk === "**thinking...**") {
        return;
      }

      streamedResponse += chunk;
      if (onStream) {
        onStream(chunk);
      }
    };

    try {
      await apiService.sendMessage(
        currentChatId,
        message.content,
        streamHandler,
        token
      );
    } catch (error) {
      // Check if this is a session expired error
      if (apiService.isSessionExpiredError(error)) {
        console.log('Session expired, attempting to rebuild from chat history...');
        
        try {
          // Rebuild the session using only the prior conversation history —
          // exclude the pending (not-yet-answered) message we just pushed
          // above, since it will be sent again by the retry below. Including
          // it here would cause the server-side history to contain the
          // question twice.
          const chatHistoryForRebuild = {
            ...chat,
            messages: chat.messages.slice(0, -1)
          };
          const rebuiltChat = await this.rebuildChatSession(chatHistoryForRebuild, token);
          
          // Update references and retry once with new session ID
          currentChatId = rebuiltChat.id;
          chat.id = rebuiltChat.id;
          
          // Clear any partial response before retrying
          streamedResponse = "";
          
          // Retry the message with the new session ID
          await apiService.sendMessage(
            currentChatId,
            message.content,
            streamHandler,
            token
          );
        } catch (rebuildError) {
          console.error('Failed to rebuild session and retry message:', rebuildError);
          throw new Error('Chat session expired and could not be restored. Please start a new chat.');
        }
      } else {
        apiService.handleError(error as Error | Response);
        throw error;
      }
    }

    // Add final response to chat record
    chat.messages.push({
      content: streamedResponse,
      role: 'assistant',
      timestamp: Date.now()
    });

    // Save updated chat (title or messages)
    try {
      await this.saveChat(chat);
    } catch (error) {
      apiService.handleError(error as Error | Response);
      throw error;
    }

    return chat;
  }

  async renameChat(chatId: string, newTitle: string): Promise<void> {
    const chat = await this.getChat(chatId);
    if (!chat) {
      throw new Error('Chat not found');
    }

    try {
      chat.title = newTitle;
      chat.updatedAt = Date.now();
      await this.saveChat(chat);
    } catch (error) {
      apiService.handleError(error as Error | Response);
      throw error;
    }
  }

  async deleteChat(chatId: string): Promise<void> {
    const user = authService.getCurrentUser();
    if (!user) {
      throw new Error('User must be authenticated to delete a chat');
    }

    try {
      const chat = await this.getChat(chatId);
      if (!chat) {
        throw new Error('Chat not found');
      }

      if (chat.userId !== user.uid) {
        throw new Error('Unauthorized access to chat');
      }

    await firebaseService.deleteDocument(this.COLLECTION_NAME, chatId);
    } catch (error) {
      apiService.handleError(error as Error | Response);
      throw error;
    }
  }

  // Export feedback service for easier access
  get feedback() {
    return feedbackService;
  }
}

export const chatService = new ChatService();